import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { Field, Form } from "react-final-form";
import renderTextField from "components/textBox";
import {
  required,
  emailField,
  specailChar,
  validatePhone,
} from "constants/validations";
import { NotificationManager } from "react-notifications";
import axios from "constants/axios";
import OTPInput from "components/GuestUserOTPVerification/OTPVerification";
import "./index.css";
import { isAxiosError } from "axios";

const UserInfoDialog = ({ open, onClose, onSuccess }) => {
  const [showOtp, setShowOtp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const [messageConsent, setMessageConsent] = useState(false);
  const debounceTimerRef = useRef(null);
  const previousPhoneRef = useRef("");
  const formRef = useRef(null);

  const [doesBrowseUserVerified, setdoesBrowseUserVerified] = useState(false);

  const composeValidators = useCallback(
    (...validators) =>
      (value, allValues) =>
        validators.reduce(
          (error, validator) => error || validator(value, allValues),
          undefined,
        ),
    [],
  );

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const fetchUserByPhone = useCallback(async (phone) => {
    if (!phone || phone.length !== 10 || !formRef.current) {
      return;
    }

    if (phone === previousPhoneRef.current) {
      return;
    }

    previousPhoneRef.current = phone;
    setIsFetchingUser(true);

    try {
      const { data } = await axios.get(
        `/tempUser/getUserByPhone?phone=${phone}`,
      );
      if (data?.error) {
        throw new Error(data?.title);
      }
      if (data?.user) {
        const { name, email, city, company_name } = data.user;
        setdoesBrowseUserVerified(!!data?.user?.is_verified);
        formRef.current.batch(() => {
          if (name) formRef.current.change("name", name);
          if (email) formRef.current.change("email", email);
          if (city) formRef.current.change("city", city);
          if (company_name) formRef.current.change("shop_name", company_name);
          setMessageConsent(!!data?.user?.message_consent);
        });
        NotificationManager.info("User details auto-filled");
      } else {
        setdoesBrowseUserVerified(false);
        formRef.current.batch(() => {
          formRef.current.change("name", "");
          formRef.current.change("email", "");
          formRef.current.change("city", "");
          formRef.current.change("shop_name", "");
          setMessageConsent(false);
        });
      }
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : isAxiosError(err)
            ? err?.response?.data?.message
            : "Error fetching user data";
      console.error(msg);
      NotificationManager.error(msg);
    } finally {
      setIsFetchingUser(false);
    }
  }, []);

  const debouncedFetchUser = useCallback(
    (phone) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (!phone || phone.length !== 10) {
        return;
      }

      debounceTimerRef.current = setTimeout(() => {
        fetchUserByPhone(phone);
      }, 800);
    },
    [fetchUserByPhone],
  );

  const onCreateBrowsingSession = (token) => {
    if (!token) {
      throw new Error("No token received");
    }

    localStorage.setItem("temp_token", token);
    localStorage.setItem("start_time", new Date().toISOString());

    onSuccess?.();
  };

  const onSubmit = useCallback(
    async (values) => {
      if (!values.phone || values.phone.length !== 10) {
        NotificationManager.error("Please enter a valid 10-digit phone number");
        return;
      }
      values.company_name = values.shop_name;
      values.message_consent = messageConsent;
      setIsLoading(true);
      
      try {
        const { data } = await axios.post("/tempUser/create", values);
        if (data?.error) {
          throw new Error(data?.title);
        }
        onCreateBrowsingSession(data?.token);

        NotificationManager.success(
          data?.title ?? "Browsing session created successfully",
        );
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 500);
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : isAxiosError(err)
              ? err?.response?.data?.message
              : "Failed to create browsing session";
        console.error(msg);
        NotificationManager.error(msg);
      } finally {
        setIsLoading(false);
      }
    },
    [onClose, onSuccess, messageConsent],
  );

  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  const PhoneField = useCallback(
    (props) => {
      const { input, meta, ...rest } = props;

      const handleChange = (e) => {
        const value = e.target.value?.replace(/\D/g, "").slice(0, 10);
        input.onChange(value);
        debouncedFetchUser(value);
      };

      const handleBlur = (e) => {
        input.onBlur(e);
        const value = input.value;
        if (value && value.length === 10) {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          fetchUserByPhone(value);
        }
      };

      return renderTextField({
        ...rest,
        input: {
          ...input,
          onChange: handleChange,
          onBlur: handleBlur,
        },
        meta,
      });
    },
    [debouncedFetchUser, fetchUserByPhone],
  );
  //Handle Send OTP
  const handleSendOtp = async (values) => {
    if (!values.phone || values.phone.length !== 10) {
      NotificationManager.error("Please enter a valid 10-digit phone number");
      return;
    }
    values.company_name = values.shop_name;
    values.message_consent = messageConsent;
    try {
      setIsLoading(true);
      const { data } = await axios.post("/tempUser/sendOtp", values);
      if (data.error) {
        throw new Error(data?.title);
      }
      setShowOtp(true);
      NotificationManager.success(
        data?.title ?? "OTP sent to Whatsapp Successfully",
      );
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : isAxiosError(err)
            ? err?.response?.data?.message
            : "Failed to create browsing session";
      console.error(msg);
      NotificationManager.error(msg);
    } finally {
      setIsLoading(false);
      handleClose();
    }
  };

  return (
    <>
      <OTPInput
        phone={previousPhoneRef.current}
        open={showOtp}
        handleClose={() => {
          setShowOtp(false);
        }}
        onSuccess={onCreateBrowsingSession}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown={isLoading}
      >
        <DialogTitle>
          <h2 style={{ margin: 0, textAlign: "center", fontSize: "1.5rem" }}>
            Start Browsing Session
          </h2>
          <p
            style={{
              margin: "8px 0 0",
              textAlign: "center",
              fontSize: "0.875rem",
              color: "#666",
            }}
          >
            Enter your details to continue
          </p>
        </DialogTitle>

        <DialogContent>
          <Form
            onSubmit={doesBrowseUserVerified ? onSubmit : handleSendOtp}
            render={({ handleSubmit, form, submitting, pristine }) => {
              // Store form reference
              formRef.current = form;
              // console.log("Values", values);
              return (
                <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                  <Field
                    name="phone"
                    component={PhoneField}
                    label="Phone Number *"
                    placeholder="Enter 10-digit phone number"
                    validate={validatePhone}
                    disabled={isLoading}
                    fullWidth
                    margin="normal"
                    helperText={
                      isFetchingUser ? "Checking for existing user..." : ""
                    }
                  />
                  <Field
                    name="name"
                    component={renderTextField}
                    label="Full Name *"
                    placeholder="Enter your full name"
                    validate={composeValidators(required, specailChar)}
                    disabled={isLoading || isFetchingUser}
                    fullWidth
                    margin="normal"
                  />
                  <Field
                    name="email"
                    component={renderTextField}
                    label="Email *"
                    placeholder="your.email@example.com"
                    validate={emailField}
                    type="email"
                    disabled={isLoading || isFetchingUser}
                    fullWidth
                    margin="normal"
                  />
                  <Field
                    name="city"
                    component={renderTextField}
                    label="City *"
                    placeholder="Enter your city"
                    validate={composeValidators(required, specailChar)}
                    disabled={isLoading || isFetchingUser}
                    fullWidth
                    margin="normal"
                  />
                  <Field
                    name="shop_name"
                    component={renderTextField}
                    label="Shop/Business Name *"
                    placeholder="Enter shop name"
                    validate={composeValidators(required, specailChar)}
                    disabled={isLoading || isFetchingUser}
                    fullWidth
                    margin="normal"
                  />

                  <FormControlLabel
                    sx={{ mt: "8px", mb: "8px" }}
                    control={
                      <Checkbox
                        checked={messageConsent}
                        onChange={(e) => setMessageConsent(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" color="textSecondary">
                        I confirm that all information entered here is correct
                        and consent to receive marketing and promotional
                        communication via{" "}
                        <strong>SMS / WhatsApp & Email</strong>
                      </Typography>
                    }
                  />

                  <DialogActions
                    style={{
                      padding: "16px 0 0",
                      justifyContent: "space-between",
                    }}
                  >
                    <Button
                      onClick={handleClose}
                      color="secondary"
                      disabled={isLoading || isFetchingUser}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      disabled={
                        submitting ||
                        pristine ||
                        isLoading ||
                        isFetchingUser ||
                        !messageConsent
                      }
                    >
                      {isLoading
                        ? doesBrowseUserVerified
                          ? "Starting Browsing..."
                          : "Sending OTP..."
                        : isFetchingUser
                          ? "Loading..."
                          : doesBrowseUserVerified
                            ? "Start Browsing"
                            : "Send OTP"}
                    </Button>
                  </DialogActions>
                </form>
              );
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserInfoDialog;
